import { faQuestionCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Chart,
  CategoryScale,
  LineController,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { statsColor } from '../../assets/colours';
import { ChartModal } from '../../shared/modal/ChartModal';
import { formatAssessmentDate } from '../../shared/util/helperfunctions';
import {
  averageIsDisplayable,
  isMissingChartValue,
  normalizeSeriesValues,
  seriesHasDisplayablePoint,
} from '../utils/chartDataAvailability';
import ChartEmptyState from './ChartEmptyState';

const monthsArr = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];

const getStat = (value, units, yMaxLimit, outOfYMax) => {
  let stat;
  if (isMissingChartValue(value)) {
    stat = 'No data';
  } else if (yMaxLimit && outOfYMax && value !== 0.1) {
    stat = `${Number(value).toString()}/${yMaxLimit}`;
  } else if (yMaxLimit && outOfYMax && value === 0.1) {
    stat = `0/${yMaxLimit}`;
  } else if (value === 0.1) {
    stat = `0 ${units}`;
  } else {
    stat = `${Number(value).toString()} ${units}`;
  }
  return stat;
};

const createCustomTooltip = (
  units,
  average_type,
  data_length,
  assessDates,
  yMaxLimit,
  outOfYMax
) => ({
  id: 'customTooltip',

  beforeDatasetsDraw(chart, args, options) {
    const {
      ctx,
      chartArea: { top, bottom, left, right, width, height },
      scales: { x, y },
    } = chart;
    if (chart.tooltip?._active[0]) {
      const activePoint = chart.tooltip._active[0];
      const datapoint = activePoint.index;
      const { datasetIndex } = activePoint;
      const value = chart.getDatasetMeta(datasetIndex).data[datapoint].$context.raw;
      const xCoor = chart.getDatasetMeta(datasetIndex).data[datapoint].tooltipPosition().x;
      const yCoor = chart.getDatasetMeta(datasetIndex).data[datapoint].tooltipPosition().y;
      // if (value > 0) {
      // draw annotation line
      ctx.save();
      ctx.beginPath();
      ctx.lineWidth = 1;
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
      ctx.moveTo(xCoor, top);
      ctx.lineTo(xCoor, bottom);
      ctx.stroke();
      ctx.closePath();

      // draw data point
      ctx.beginPath();
      ctx.lineWidth = 1;
      ctx.strokeStyle = '#e92364';
      ctx.fillStyle = '#e92364';
      ctx.arc(xCoor, yCoor, 3, 0, 2 * Math.PI, false);
      ctx.fill();
      ctx.stroke();
      ctx.closePath();
      // }
    }
  },
  afterDraw(chart, args, options) {
    if (chart.tooltip?._active[0]) {
      const {
        ctx,
        chartArea: { top, bottom },
      } = chart;
      ctx.save();
      const activePoint = chart.tooltip._active[0];
      const datapoint = activePoint.index;
      const { datasetIndex } = activePoint;

      // get text for tooltip
      const value = chart.getDatasetMeta(datasetIndex).data[datapoint].$context.raw;
      const stat = getStat(value, units, yMaxLimit, outOfYMax);
      const date = chart.getDatasetMeta(datasetIndex).iScale._labelItems[datapoint].label;
      const textWidth = ctx.measureText(date).width;

      // coordinates
      const xPoint = chart.getDatasetMeta(datasetIndex).data[datapoint].tooltipPosition().x;
      const yPoint = chart.getDatasetMeta(datasetIndex).data[datapoint].tooltipPosition().y;

      const labelsLength = chart.getDatasetMeta(datasetIndex).iScale._labelItems.length;

      const activeLabel = chart.getDatasetMeta(datasetIndex).data[datapoint].$context.index;

      // for the last two values the tooltip needs to be on the left of the active point
      if (activeLabel >= labelsLength - 2) {
        if (data_length !== 7 && !assessDates) {
          // draw rounded rectangle
          ctx.beginPath();
          ctx.roundRect(xPoint - 105, yPoint - 25, 110, 65, 12);
          ctx.strokeStyle = '#dccfdb';
          ctx.lineWidth = 1;
          ctx.stroke();
          ctx.fillStyle = '#dccfdb';
          ctx.fill();
          ctx.closePath();

          // draw date
          ctx.font = '10px futura-pt-book';
          ctx.fillStyle = statsColor;
          ctx.textAlign = 'start';
          ctx.fillText(`${date[0]} ${date[1]}`, xPoint - 90, yPoint - 5);

          // draw average
          ctx.font = '15px futura-pt-book';
          ctx.fillStyle = statsColor;
          ctx.textAlign = 'start';
          ctx.fillText(`${average_type} `, xPoint - 90, yPoint + 10);

          // draw value
          ctx.font = '15px futura-pt-demi';
          ctx.fillStyle = statsColor;
          ctx.textAlign = 'start';
          ctx.fillText(`${stat} `, xPoint - 90, yPoint + 25);
        } else {
          // draw rounded rectangle
          ctx.beginPath();
          ctx.roundRect(xPoint - 105, yPoint - 25, 90, 50, 12);
          ctx.strokeStyle = '#dccfdb';
          ctx.lineWidth = 1;
          ctx.stroke();
          ctx.fillStyle = '#dccfdb';
          ctx.fill();
          ctx.closePath();

          // draw date
          ctx.font = '10px futura-pt-book';
          ctx.fillStyle = statsColor;
          ctx.textAlign = 'start';
          ctx.fillText(`${date[0]} ${date[1]}`, xPoint - 90, yPoint - 5);

          // draw value
          ctx.font = '15px futura-pt-demi';
          ctx.fillStyle = statsColor;
          ctx.textAlign = 'start';
          ctx.fillText(`${stat} `, xPoint - 90, yPoint + 10);
        }
      } else if (data_length !== 7 && !assessDates) {
        // draw rounded rectangle
        ctx.beginPath();
        ctx.roundRect(xPoint + 15, yPoint + 25, 110, -65, 12);
        ctx.strokeStyle = '#dccfdb';
        ctx.lineWidth = 1;
        ctx.stroke();
        ctx.fillStyle = '#dccfdb';
        ctx.fill();
        ctx.closePath();

        // draw date
        ctx.font = '10px futura-pt-book';
        ctx.fillStyle = statsColor;
        ctx.textAlign = 'start';
        ctx.fillText(`${date[0]} ${date[1]}`, xPoint + 25, yPoint - 18);

        // draw average type
        ctx.font = '15px futura-pt-book';
        ctx.fillStyle = statsColor;
        ctx.textAlign = 'start';
        ctx.fillText(`${average_type}`, xPoint + 25, yPoint - 3);

        // draw value
        ctx.font = '15px futura-pt-demi';
        ctx.fillStyle = statsColor;
        ctx.textAlign = 'start';
        ctx.fillText(`${stat}`, xPoint + 25, yPoint + 12);
      } else {
        // draw rounded rectangle
        ctx.beginPath();
        ctx.roundRect(xPoint + 15, yPoint + 25, 90, -50, 12);
        ctx.strokeStyle = '#dccfdb';
        ctx.lineWidth = 1;
        ctx.stroke();
        ctx.fillStyle = '#dccfdb';
        ctx.fill();
        ctx.closePath();

        // draw date
        ctx.font = '10px futura-pt-book';
        ctx.fillStyle = statsColor;
        ctx.textAlign = 'start';
        ctx.fillText(`${date[0]} ${date[1]}`, xPoint + 25, yPoint - 5);

        // draw value
        ctx.font = '15px futura-pt-demi';
        ctx.fillStyle = statsColor;
        ctx.textAlign = 'start';
        ctx.fillText(`${stat}`, xPoint + 25, yPoint + 10);
      }
      ctx.restore();
    }
  },
});

function LineChart(props) {
  const { t } = useTranslation();
  const titleId = props.title?.replace(/\s+/g, '');
  const showChartShellOnEmpty = Boolean(props.showChartShellOnEmpty);
  const [dataAvailable, setDataAvailable] = useState(true);
  const [showChartDescription, setShowChartDescription] = useState(false);
  const [tableData, setTableData] = useState([]);

  const chartRef1 = useRef(null);

  useEffect(() => {
    Chart.register(
      CategoryScale,
      LineController,
      LinearScale,
      PointElement,
      LineElement,
      Title,
      Tooltip,
      Legend
    );

    const labels = [];
    const dataArr = [];
    const labelColor = [];
    let highest = 0;
    let assessmentDate;
    let dateIndex;
    let stepSize = 2;
    if (props.assessDates) {
      assessmentDate = formatAssessmentDate(props.assessDates[props.assessmentNumber]).slice(0, -5);
    } else {
      const currentDate = new Date().toJSON().slice(0, 10);
      assessmentDate = formatAssessmentDate(currentDate).slice(0, 6);
    }
    const normalizedData = normalizeSeriesValues(props.data);
    const hasDisplayableData = seriesHasDisplayablePoint(normalizedData);

    if (normalizedData.length > 0) {
      normalizedData.forEach((item, index) => {
        let { date } = item;
        if (!props.assessDates && props.data.length !== 12) {
          date = formatAssessmentDate(date.slice(0, 10)).slice(0, 6);
        } else if (!props.assessDates && props.data.length === 12) {
          const newDate = new Date(date);
          date = newDate.getFullYear().toString();
        }
        if (date.startsWith('0')) date = date.substring(1);
        if (assessmentDate === date) {
          labelColor.push('#fff');
          dateIndex = index;
        } else {
          labelColor.push('rgba(221,206,219, 0.5)');
        }
        date = date.toUpperCase();
        if (!props.assessDates && props.data.length === 5) {
          labels.push([date.split(' ')[1], `WEEK ${index + 1}`]);
        } else if (!props.assessDates && props.data.length === 12) {
          labels.push([monthsArr[index], date.substring(2)]);
        } else {
          labels.push([date.split(' ')[0], date.split(' ')[1]]);
        }
        dataArr.push(item.value);
      });

      // if there are less than 5 assessments, add empty data
      if (dataArr.length < 5 && dataArr.length > 0) {
        for (let x = dataArr.length; x < 5; x++) {
          labelColor.push('rgba(221,206,219, 0.5)');
          dataArr.push(null);
          labels.push(['DD', 'MM']);
        }
      }

      const numericValues = dataArr.filter((item) => item !== null && item !== undefined);
      highest = numericValues.length > 0 ? Math.max(...numericValues) : 0;
      if (props.yMaxLimit) {
        highest = props.yMaxLimit;
      } else if (highest <= 20) {
        highest = Math.ceil(highest / 5) * 5;
      } else {
        highest = Math.ceil(highest / 10) * 10;
      }

      // Keep a visible chart shell for empty-state mode on dynamic scales.
      // Optional `emptyShellYMax` lets a section tune empty-state visuals only.
      if (!hasDisplayableData && showChartShellOnEmpty) {
        if (props.emptyShellYMax) {
          highest = props.emptyShellYMax;
        } else if (highest === 0) {
          highest = 1;
        }
      }

      if (highest <= 5) {
        stepSize = 1;
      } else if (highest <= 15) {
        highest % 3 === 0 ? (stepSize = 3) : (stepSize = 2);
      } else {
        stepSize = 4;
      }

      setDataAvailable(hasDisplayableData);
    } else {
      setDataAvailable(false);
    }
    const tableData = normalizedData.map((item, index) => ({
      date: labels[index] ? `${labels[index][0]} ${labels[index][1]}` : item.date,
      value: getStat(item.value, props.units, props.yMaxLimit, props.outOfYMax),
    }));
    setTableData(tableData);
    const data = {
      labels,
      datasets: [
        {
          data: dataArr,
          fill: false,
          borderColor: `${props.average === null ? '#615668' : 'rgb(233, 35, 100)'}`,
          backgroundColor: 'rgb(233, 35, 100)',
          tension: 0,
        },
      ],
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      spanGaps: true,
      layout: {
        padding: {
          top: 50,
          right: 20,
          bottom: 0,
          left: 0,
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        title: {
          display: false,
        },
        tooltip: {
          enabled: false,
        },
      },

      scales: {
        y: {
          max: highest,
          min: 0,
          beginAtZero: true,
          grid: {
            color: 'rgba(221, 206, 219, 0.2)',
          },
          border: {
            display: false,
          },
          position: 'right',
          ticks: {
            display: hasDisplayableData,
            stepSize,
          },
        },
        x: {
          ticks: {
            font: 'futura-pt-medium',
            size: 12.5,
            color: labelColor,
          },
          grid: {
            display: false,
          },
        },
      },
      elements: {
        point: {
          radius: 3,
          hitRadius: 15, // Increase as needed
          pointStyle: (context) => {
            const value = context.dataset.data[context.dataIndex];
            return value === null || value === undefined ? false : 'circle';
          },
        },
      },
      onHover: (event, activeElements) => {
        const { chart } = event;

        if (chart && hasDisplayableData) {
          labelColor.forEach((item, index) => {
            if (index === dateIndex) {
              labelColor[index] = '#fff';
            } else {
              labelColor[index] = 'rgba(221,206,219, 0.5)';
            }
          });

          if (activeElements.length > 0) {
            activeElements.forEach((activeElement) => {
              const { index } = activeElement;

              if (index === dateIndex) {
                labelColor[index] = '#fff';
              } else {
                labelColor[index] = 'rgba(221,206,219, 1)';
              }
            });
          }

          chart.config.options.scales.x.ticks.color = labelColor;

          chart.update();
        }
      },
    };

    const shouldRenderCanvas = hasDisplayableData || showChartShellOnEmpty;
    if (chartRef1?.current && shouldRenderCanvas) {
      const { chartInstance } = chartRef1.current;
      if (chartInstance) {
        chartInstance.destroy();
      }
      chartRef1.current.chartInstance = new Chart(chartRef1.current.getContext('2d'), {
        type: 'line',
        data,
        options,
        plugins: [
          createCustomTooltip(
            props.units,
            props.average_type,
            normalizedData.length,
            props.assessDates,
            props.yMaxLimit,
            props.outOfYMax
          ),
        ],
      });
    } else if (chartRef1?.current) {
      const { chartInstance } = chartRef1.current;
      if (chartInstance) {
        chartInstance.destroy();
      }
    }

    return () => {
      if (chartRef1 && chartRef1.current && chartRef1.current.chartInstance) {
        chartRef1.current.chartInstance.destroy();
      }
    };
  }, [props.data, props.assessmentNumber, props.average]);

  if (!dataAvailable && !showChartShellOnEmpty) {
    return (
      <ChartEmptyState
        title={props.title}
        width={props.width}
        openModal={props.openModal}
        modalText={props.modalText}
      />
    );
  }

  return (
    <div
      className={`chart-container ${props.width}`}
      role="group"
      aria-labelledby={`${titleId}-line-chart-title`}
    >
      <div className="title-container">
        <h3 className="title" id={`${titleId}-line-chart-title`}>
          {props.title}
        </h3>
        <button
          className="wtf transparent-button"
          onClick={() => props.openModal(props.title, props.modalText)}
        >
          <span className="visuallyHidden">{t('open.modal.chart.information')}</span>
          <FontAwesomeIcon icon={faQuestionCircle} className="wtf icon" />
        </button>
      </div>
      <div className="charts-box fs-mask">
        {!dataAvailable && showChartShellOnEmpty && (
          <div className="chart-no-data">{t('chart.noDataAvailable')}</div>
        )}
        <div className="chart-description-container">
          <div className="chart-wrap fs-mask">
            <canvas
              role="img" // eslint-disable-line
              ref={chartRef1}
              aria-label={`${t('chart.line.description')} ${props.title} ${props.assessmentNumber ? t('results.assessment.data') : t('results.tracker.data')}`}
              aria-hidden={!dataAvailable}
            />
          </div>
          {dataAvailable && (
            <button
              className="chart-description fs-exclude"
              onClick={() => setShowChartDescription(true)}
            >
              {t('results.modal.button')}
              <span className="sr-only">{t('results.modal.button.description')}</span>
            </button>
          )}
        </div>
        {dataAvailable && showChartDescription && (
          <ChartModal
            title={`${props.title.toLowerCase()} ${props.assessmentNumber ? 'assessment data' : 'tracker data'}`}
            onClose={() => setShowChartDescription(false)}
            data={tableData}
          />
        )}
        <div className="average">
          <div className="title">{props.average_type}</div>
          <div className="stats" style={{ color: props.color }}>
            <FontAwesomeIcon icon={props.icon} className="icon alarm" />
            <div className="stat">
              {averageIsDisplayable(props.average) ? Number(props.average).toString() : ' '}
              {/* {props.units && <span>{props.units}</span>} */}
            </div>
            {(props.units || !averageIsDisplayable(props.average)) && (
              <div className={`unit ${averageIsDisplayable(props.average) ? '' : 'no-data-unit'}`}>
                {averageIsDisplayable(props.average) ? props.units : t('chart.noDataAvailable')}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
export default LineChart;
